import type { ReactNode } from 'react';

import { WarningIcon } from '@/components/icons';
import { cn } from '@/lib/utils';

/**
 * Inline notice — the shared presentational shape of the three
 * near-clone banners that already live on `main`
 * (`StorageUnavailableWarning` / `SaveTransientWarning` /
 * `SaveRejectedWarning` in `record-editor-route.tsx`).
 *
 * This is a **same-shape extraction**, not a new design primitive
 * (req 004 Phase A slice 2; brief Q5 — reuse the existing inline
 * `role="status"` / `role="alert"` surfaces, no new Toast/Banner
 * primitive). The harvest-candidate `Callout`/`Alert` stays design's
 * call per `apps/client/docs/design.md` §"Workflow"; here we only
 * consolidate the inline pattern so the per-action graceful-failure
 * notices (list-load / create / delete / re-analyze) don't re-clone
 * it a fourth, fifth, sixth time.
 *
 * Two tones (req 006 two-tone notice register), where the polite/assertive
 * ARIA split lines up with the system/error split:
 *
 *   - `tone="status"` → `role="status"` + `aria-live="polite"`, the
 *     **system** register (primary blue). Informational / recoverable state
 *     ("you're offline — saved locally" / "try again in a moment").
 *   - `tone="alert"`  → `role="alert"`, the **error** register (pulled-back
 *     destructive red). A thing the user did just failed.
 *
 * Presentational only — no store coupling. Each call site passes its
 * own message and, where layout needs it, a positional `className`
 * (margins/spacing); the component owns the border/background/text and
 * the leading glyph so those can't drift between call sites.
 *
 * NOTE (convergence): the four hand-rolled editor-route banners
 * (`OfflineBanner` / `StorageUnavailableWarning` / `SaveTransientWarning`
 * / `SaveRejectedWarning` in `record-editor-route.tsx`) are deliberately
 * left as-is — converging them onto this helper happens in the chunk that
 * owns that route file, not here. The locked banner→tone mapping is
 * Offline / StorageUnavailable / SaveTransient → `tone="status"`,
 * SaveRejected → `tone="alert"`; all four are expressible by the two
 * existing tones (no extension needed).
 */

export interface InlineNoticeProps {
  /**
   * `status` — system register (primary blue), polite `role="status"`
   * (informational / recoverable). `alert` — error register (pulled-back
   * destructive red), assertive `role="alert"` (something failed).
   */
  tone: 'status' | 'alert';
  /** The notice body. `ReactNode` so callers can pass rich text / entities. */
  children: ReactNode;
  /**
   * Optional right-aligned action slot (e.g. buttons). When present the notice
   * renders as a single-row banner: the leading glyph + message sit on the
   * left and the actions are pushed to the right edge, all vertically centered
   * on one line. Omit it — the default — and the notice keeps its top-aligned
   * glyph + stacked content, which is the right shape for a multi-line
   * informational message (the offline / storage / save banners).
   */
  actions?: ReactNode;
  /** Optional positional className (margins/spacing) for the call site. */
  className?: string;
  /** Optional test id passthrough. */
  'data-testid'?: string;
}

const TONE_CLASSES: Record<InlineNoticeProps['tone'], string> = {
  // system — primary-blue register for informational / recoverable state
  // (offline, storage). Resolves through the --notice-system* token triplet;
  // the dark cascade is owned by the token, not a `dark:` utility chain.
  status: 'border-notice-system-border bg-notice-system text-notice-system-foreground',
  // error — pulled-back destructive-red register for a failure the user hit.
  alert: 'border-notice-error-border bg-notice-error text-notice-error-foreground',
};

export function InlineNotice({
  tone,
  children,
  actions,
  className,
  'data-testid': dataTestId,
}: InlineNoticeProps) {
  const isStatus = tone === 'status';
  const hasActions = actions != null;
  return (
    <div
      role={isStatus ? 'status' : 'alert'}
      aria-live={isStatus ? 'polite' : undefined}
      data-testid={dataTestId}
      className={cn(
        'flex gap-3 rounded-md border px-4 py-3 text-sm',
        // Center everything on one line when there are actions (a banner the
        // user acts on); otherwise top-align the glyph against the first line
        // of (potentially multi-line) message text.
        hasActions ? 'items-center' : 'items-start',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <WarningIcon aria-hidden="true" className={cn('size-4 shrink-0', !hasActions && 'mt-0.5')} />
      {/* flex-1 so the message column fills the banner width and the actions
          land at the right edge; space-y-1 keeps multi-paragraph bodies tidy. */}
      <div className={cn('space-y-1', hasActions && 'flex-1')}>{children}</div>
      {hasActions && (
        <div className="flex shrink-0 items-center gap-(--space-button-group)">{actions}</div>
      )}
    </div>
  );
}
